import { AnchorProvider, Program } from '@coral-xyz/anchor';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useMemo } from 'react';
import idl from '@/lib/anchor/tower_of_power.json';
import { PublicKey, SystemProgram, Keypair } from '@solana/web3.js';

// Simple SHA-256 hash function using Web Crypto API
async function sha256(data: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  return new Uint8Array(hashBuffer);
}

// The IDL type from the raw JSON file
type RawIdl = {
  address: string;
  metadata: any;
  instructions: any[];
  types?: any[];
  accounts?: any[];
  [key: string]: any;
};

export function useProgram() {
  const { connection } = useConnection();
  const wallet = useWallet();

  const provider = useMemo(() => {
    // Ensure wallet has required methods before creating provider
    if (!wallet.signTransaction || !wallet.publicKey) {
      return null;
    }
    
    return new AnchorProvider(connection, wallet as any, {
      commitment: 'confirmed',
    });
  }, [connection, wallet]);

  const program = useMemo(() => {
    if (!provider) return null;
    
    const patchedIdl = { ...idl } as RawIdl;

    // In-memory patch for the IDL.
    // Modern Anchor clients expect state accounts in a top-level `accounts` array,
    // but older generators put them in `types`. We move all types to be safe.
    if (patchedIdl.types && !patchedIdl.accounts) {
        patchedIdl.accounts = patchedIdl.types;
    }
    
    return new Program(patchedIdl as any, provider);
  }, [provider]);

  // Helper function to create Web2 player ID hash
  const createWeb2IdHash = async (userId: string): Promise<Uint8Array> => {
    return await sha256(userId);
  };

  // DEPRECATED: Web2 player initialization
  // PDA creation has been moved to the backend.
  // Use /api/players/initialize-web2 endpoint instead.
  // This function is kept for backward compatibility but should not be used.
  const initializeWeb2Player = async (
    name: string, 
    characterClass: number = 0,
    userId: string,
    serverKeypair: Keypair
  ) => {
    throw new Error(
      'initializeWeb2Player is deprecated. ' +
      'PDA creation has been moved to the backend. ' +
      'Use /api/players/initialize-web2 endpoint instead.'
    );
  };

  return {
    program,
    provider,
    initializeWeb2Player,
    createWeb2IdHash,
  };
} 